import { Offer as OfferResource } from "@/js/resources";
import CircleCheck from "@/svg/circle-check-solid.svg?react";
import CirlceXMark from "@/svg/circle-xmark-solid.svg?react";
import Crown from "@/svg/crown-duotone-solid.svg?react";
import clsx from "clsx";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import Button from "./form/Button";
import Link from "./Link";

interface OfferProps extends Omit<OfferResource, "id"> {
    className?: string,
    button?: {
        to?: string,
        onClick?: () => Promise<void>,
        children: React.ReactNode
    },
    noMissingFeatures?: boolean
}

function Feature({
    className,
    variant,
    children
}: {
    className?: string,
    variant: "yes" | "no",
    children: React.ReactNode
}) {
    return (
        <div className={clsx("flex items-center gap-2 body-l-md", {
            "text-text-800 fill-white": variant === "yes",
            "text-text-600 fill-neutral-800": variant === "no"
        }, className)}>
            {variant === "yes" ? (
                <CircleCheck className="size-5" />
            ) : (
                <CirlceXMark className="size-5" />
            )}
            {children}
        </div>
    )
}

export default function Offer({
    className,
    offer,
    onClick,
    onReject,
    noMissingFeatures,
    loggedIn
}: {
    className?: string,
    offer: OfferResource,
    onClick?: () => void | Promise<void>,
    onReject?: () => void,
    noMissingFeatures?: boolean,
    loggedIn?: boolean
}) {
    const {t, i18n} = useTranslation();

    return (
        <div className={clsx("max-w-95 w-full rounded-xl shadow-offer overflow-hidden", {
            "border border-primary-900": offer.highlight
        }, className)}>
            {offer.highlight && (
                <div className="flex h-12 justify-center items-center gap-3 font-semibold text-2xl text-primary-500 bg-primary-900">
                    <Crown className="h-6 fill-primary-500" />
                    {t("offer.bestOffer")}
                </div>
            )}
            <div className="flex h-12 justify-center items-center bg-bg-900 font-semibold text-2xl text-primary-600">
                {offer.trial_days ? (
                    t("offer.title.trial", {days: offer.trial_days})
                ) : offer.period === "monthly" ? (
                    t("offer.title.monthly")
                ) : offer.period === "yearly" ? (
                    t("offer.title.yearly")
                ) : (
                    t("offer.title.biyearly")
                )}
            </div>
            <div className="flex flex-col px-8 py-10 bg-bg-500 items-center">
                <div className="flex gap-2.5 items-baseline font-medium text-xl text-neutral-800">
                    {offer.trial_days ? (
                        <Trans i18nKey="offer.trial">
                            <span className="font-bold text-7xl text-white">{{price: (offer.trial_price! / 100).toLocaleString(i18n.language, {
                                currency: "eur",
                                style: "currency",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })} as any}</span> for {{days: offer.trial_days}} days
                        </Trans>
                    ) : (
                        <Trans i18nKey="offer.monthlyPrice">
                            <span className="font-bold text-7xl text-white">{{price: ((() => {
                                switch (offer.period) {
                                    case "monthly": 
                                        return offer.price;
                                    case "yearly": 
                                        return offer.price / 12;
                                    case "biyearly":
                                        return offer.price / 24;
                                }
                            })() / 100).toLocaleString(i18n.language, {
                                style: "currency", 
                                currency: "eur",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })} as any}</span>
                        </Trans>
                    )}
                </div>
                <span className="mt-4 text-center text-lg text-text-700">
                    {t(`offer.${offer.period}${offer.trial_price && offer.trial_days ? "AfterTrial" : ""}`, {price: (offer.price / 100).toLocaleString(i18n.language, {
                        style: "currency",
                        currency: "eur",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })})}
                </span>
                <div className="flex flex-col mt-8 gap-4 items-center">
                    <Feature variant="yes">{t(`offer.feature.${offer.trial_days ? "trial" : offer.period}`)}</Feature>
                    {!offer.trial_days && offer.features?.includes("downloads") ? (
                        <Feature variant="yes">{t("features.download")}</Feature>
                    ) : (
                        <Feature variant="no">{t("features.noDownload")}</Feature>
                    )}
                </div>
                <Button className="mt-8 self-stretch" variant="primary" onClick={onClick}>{onReject ? loggedIn ? t("offer.button.toPayment") : t("offer.button.toSignUp") : t(`offer.button.${offer.trial_days ? "trial" : offer.period}`)}</Button>
                {onReject && (
                    <Link className="mt-5" onClick={onReject}>{t("offer.reject")}</Link>
                )}
            </div>
        </div>
    )
}